import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../../lib/api";
import { Loader2, MapPinOff } from "lucide-react";

const PALETTE = ["#2BA3A0", "#D4AF5A", "#3a8acb", "#8e44ad", "#e67e22", "#c2185b"];

function Fit({ pts }) {
  const map = useMap();
  useEffect(() => { if (pts.length === 1) map.setView(pts[0], 5); else if (pts.length > 1) map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], maxZoom: 6 }); }, [pts, map]);
  return null;
}

/** Centres Bombacare sur la carte du monde (géocodage Nominatim côté serveur). */
export function NetworkMap({ selected, onSelect }) {
  const [data, setData] = useState(() => { try { return JSON.parse(localStorage.getItem("bc_network_map") || "null"); } catch { return null; } });
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/network/map").then(({ data }) => { setData(data); localStorage.setItem("bc_network_map", JSON.stringify(data)); }).catch(() => setErr(!data)); }, []); // eslint-disable-line
  if (err) return <div data-testid="network-map-error" className="text-[13px] text-[#D9534F] p-4">Carte indisponible.</div>;
  if (!data) return <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#2BA3A0]" /></div>;
  const avec = data.centres.filter((c) => c.position);
  const pts = avec.map((c) => [c.position.lat, c.position.lng]);
  const color = (c, i) => (c.est_moi ? "#2BA3A0" : PALETTE[(i + 1) % PALETTE.length]);
  return (
    <div data-testid="network-map" className="relative z-0 rounded-2xl overflow-hidden">
      <div className="h-[420px]">
        <MapContainer center={data.centre ? [data.centre.lat, data.centre.lng] : [10, 15]} zoom={3} scrollWheelZoom className="h-full w-full">
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Fit pts={pts} />
          {avec.map((c, i) => (
            <CircleMarker key={c.id} center={[c.position.lat, c.position.lng]} radius={10 + Math.min(20, c.patients * 2)} pathOptions={{ color: selected === c.id ? "#D4AF5A" : "#fff", weight: selected === c.id ? 4 : 2, fillColor: color(c, i), fillOpacity: 0.85 }}
              eventHandlers={{ click: () => onSelect?.(c.id) }}>
              <Popup>
                <div data-testid="network-map-popup" className="min-w-[180px] flex flex-col gap-0.5">
                  <div className="font-bold text-[#0A1426]">{c.nom}{c.est_moi ? " · mon centre" : ""}</div>
                  <div className="text-[11px] text-[#0A1426]/70">{c.praticien} · {[c.ville, c.pays].filter(Boolean).join(", ")}</div>
                  <div className="text-[11px]">{c.patients} patient(s) · assiduité {c.assiduite_pct}% · {c.fruits?.total || 0} fruit(s)</div>
                  {c.parrain_id && <div className="text-[11px] text-[#0A1426]/70">Rattaché à {data.centres.find((x) => x.id === c.parrain_id)?.nom || "—"}</div>}
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>
      {data.sans_position.length > 0 && <div data-testid="network-map-nopos" className="px-3 py-2 text-[11px] text-[#0A1426]/70 flex items-center gap-1"><MapPinOff size={12} /> Sans ville renseignée : {data.sans_position.join(", ")}</div>}
    </div>
  );
}
